import React, { useState } from 'react';
import styled from '@emotion/styled';
import { Settings as SettingsIcon, User, Calendar, Lock } from 'lucide-react';
import UpdateSettingsFormContainer from './UpdateSettingsFormContainer';
import UpdateScheduleFormContainer from './UpdateScheduleFormContainer';
import PasswordChangeFormContainer from './PasswordChangeFormContainer';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px;
  
  @media (max-width: 1024px) {
    padding: 20px;
  }
  
  @media (max-width: 768px) {
    padding: 16px;
  }
  
  @media (max-width: 480px) {
    padding: 12px;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 40px;
  
  @media (max-width: 768px) {
    margin-bottom: 32px;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 24px;
  }
`;

const Title = styled.h1`
  color: #ededed;
  font-family: 'Clash Grotesk', sans-serif;
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0 0 16px 0;
  
  @media (max-width: 1024px) {
    font-size: 2.25rem;
  }
  
  @media (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: 12px;
  }
  
  @media (max-width: 480px) {
    font-size: 1.75rem;
    margin-bottom: 8px;
  }
`;

const Subtitle = styled.p`
  color: #9E9E9E;
  font-size: 1.125rem;
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

const TabContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 40px;
  gap: 16px;
  
  @media (max-width: 768px) {
    margin-bottom: 32px;
    gap: 12px;
    flex-wrap: wrap;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 24px;
    gap: 8px;
    flex-direction: column;
    align-items: center;
  }
`;

const TabButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: ${props => props.active ? '#F43F60' : 'transparent'};
  color: ${props => props.active ? '#fff' : '#9E9E9E'};
  border: 2px solid ${props => props.active ? '#F43F60' : '#2A2A2A'};
  border-radius: 12px;
  font-family: 'Clash Grotesk', sans-serif;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.active ? '#F43F60' : 'rgba(244, 63, 96, 0.1)'};
    border-color: #F43F60;
    color: ${props => props.active ? '#fff' : '#F43F60'};
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
  
  @media (max-width: 768px) {
    padding: 10px 20px;
    font-size: 0.9rem;
    gap: 6px;
    
    svg {
      width: 18px;
      height: 18px;
    }
  }
  
  @media (max-width: 480px) {
    padding: 12px 16px;
    font-size: 0.85rem;
    gap: 4px;
    width: 100%;
    max-width: 280px;
    justify-content: center;
    
    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

const ContentContainer = styled.div`
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);

`;

const Settings = () => {
  const [activeTab, setActiveTab] = useState('profile');

  const tabs = [
    { id: 'profile', label: 'Profile Settings', icon: User },
    { id: 'schedule', label: 'Schedule Settings', icon: Calendar },
    { id: 'password', label: 'Password Change', icon: Lock },
  ];

  const renderContent = () => {
    switch (activeTab) {
      case 'profile':
        return <UpdateSettingsFormContainer />;
      case 'schedule':
        return <UpdateScheduleFormContainer />;
      case 'password':
        return <PasswordChangeFormContainer />;
      default:
        return <UpdateSettingsFormContainer />;
    }
  };

    return (
    <Container>
      <Header>
        <Title>Settings</Title>
        <Subtitle>Manage your account preferences and configurations</Subtitle>
      </Header>

      <TabContainer>
        {tabs.map((tab) => {
          const IconComponent = tab.icon;
          return (
            <TabButton
              key={tab.id}
              active={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
            >
              <IconComponent />
              {tab.label}
            </TabButton>
          );
        })}
      </TabContainer>

      <ContentContainer>
        {renderContent()}
      </ContentContainer>
    </Container>
    );
};

export default Settings; 